/**
 * CraftingAbility (Foundry: rule-element/crafting-ability.ts): a crafting entry (Advanced
 * Alchemy, Quick Alchemy, a familiar's formulas) with its slots, batch sizes and what it can
 * craft. Recorded on the sheet's resources block; the crafting UI is later work.
 */
import type { CraftingEntryData } from "../actor-data";
import { isValidPredicate, type RawPredicate } from "../predicate";
import { cleanRuleValue, resolveInjectedProperties, resolveValue } from "../resolve";
import { sluggifyCamel } from "../slug";
import type { EngineState } from "../state";
import type { RuleHandler, RuleInstance } from "./base";

interface CraftingAbilityData {
  slug: string;
  resource: string | null;
  isAlchemical: boolean;
  isDailyPrep: boolean;
  isPrepared: boolean;
  batchSizes: { default: number | null; other: { quantity: number; definition: RawPredicate }[] };
  maxItemLevel: unknown;
  maxSlots: unknown;
  craftableItems: RawPredicate;
  prepared: { uuid: string; quantity?: number; expended?: boolean; isSignatureItem?: boolean }[];
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parse(rule: RuleInstance): void {
  const s = rule.source;
  const resource = typeof s.resource === "string" && s.resource ? s.resource : null;
  const slug = rule.slug ?? resource;
  if (!slug) return rule.fail("Either a resource or a slug is required");
  if (s.maxSlots !== undefined && resource) return rule.fail("Only one of resource or maxSlots is allowed");
  if (s.craftableItems !== undefined && !isValidPredicate(s.craftableItems)) return rule.fail("craftableItems: must be a predicate");
  const isAlchemical = s.isAlchemical === true;
  const isDailyPrep = s.isDailyPrep === true;
  const batch = isPlainObject(s.batchSizes) ? s.batchSizes : {};
  const other = Array.isArray(batch.other) ? batch.other.filter(isPlainObject).map((o) => ({ quantity: typeof o.quantity === "number" ? o.quantity : 2, definition: isValidPredicate(o.definition) ? o.definition : [] })) : [];
  const prepared = Array.isArray(s.prepared) ? s.prepared.filter(isPlainObject).filter((p) => typeof p.uuid === "string").map((p) => ({ uuid: String(p.uuid), ...(typeof p.quantity === "number" ? { quantity: p.quantity } : {}), ...(typeof p.expended === "boolean" ? { expended: p.expended } : {}), ...(typeof p.isSignatureItem === "boolean" ? { isSignatureItem: p.isSignatureItem } : {}) })) : [];
  if (typeof s.priority !== "number") rule.priority = 19;
  rule.slug = slug;
  rule.data = {
    slug,
    resource,
    isAlchemical,
    isDailyPrep,
    isPrepared: s.isPrepared === true || isAlchemical || isDailyPrep,
    batchSizes: { default: typeof batch.default === "number" ? batch.default : null, other },
    maxItemLevel: s.maxItemLevel === undefined ? undefined : cleanRuleValue(s.maxItemLevel),
    maxSlots: s.maxSlots === undefined ? undefined : cleanRuleValue(s.maxSlots),
    craftableItems: (s.craftableItems as RawPredicate | undefined) ?? [],
    prepared,
  } satisfies CraftingAbilityData as unknown as Record<string, unknown>;
}

function onApplyActiveEffects(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as CraftingAbilityData;
  const slug = resolveInjectedProperties(rule, state, data.slug);
  if (rule.ignored) return;
  const key = sluggifyCamel(slug);
  const maxSlots = data.maxSlots !== undefined ? Math.floor(Number(resolveValue(rule, state, data.maxSlots))) : null;
  const maxItemLevel = data.maxItemLevel === undefined ? 0 : Number(resolveValue(rule, state, data.maxItemLevel));
  if (rule.ignored) return;
  const label = state.catalogue?.localize(rule.label) ?? rule.label;
  const entries = state.actor.system.crafting.entries;
  const existing = entries[key];
  if (existing) {
    if (rule.label !== rule.item.name) existing.label = label;
    if (data.craftableItems.length > 0) existing.craftableItems.push({ predicate: structuredClone(data.craftableItems) });
    for (const other of data.batchSizes.other) existing.craftableItems.push({ predicate: structuredClone(other.definition), batchSize: other.quantity });
    existing.batchSize = Math.max(existing.batchSize, data.batchSizes.default || 1);
    existing.maxItemLevel = Math.max(maxItemLevel, existing.maxItemLevel);
    existing.maxSlots = (existing.maxSlots ?? maxSlots) === null ? null : Math.max(existing.maxSlots ?? 0, maxSlots ?? 0);
    return;
  }
  const entry: CraftingEntryData = {
    slug,
    resource: data.resource,
    label,
    isAlchemical: data.isAlchemical,
    isDailyPrep: data.isDailyPrep,
    isPrepared: data.isPrepared,
    batchSize: data.batchSizes.default ?? (data.isAlchemical ? 2 : 1),
    craftableItems: [{ predicate: structuredClone(data.craftableItems) }, ...data.batchSizes.other.map((o) => ({ predicate: structuredClone(o.definition), batchSize: o.quantity }))].filter((i) => i.predicate.length > 0),
    maxItemLevel: maxItemLevel || state.level,
    maxSlots,
    preparedFormulaData: structuredClone(data.prepared),
    source: rule.item.uuid,
    sourceName: rule.item.name,
  };
  entries[key] = entry;
  state.rollOptions.set("all", `crafting:entry:${slug}`);
}

export const CraftingAbilityHandler: RuleHandler = { key: "CraftingAbility", parse, onApplyActiveEffects };
